import { Button, formatPhone, ListView } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { type DropdownItem, DropdownMenu } from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { errorOf } from "../../api";
import { isCoordinate, toCoordinate } from "../../coordinates";
import { messageFor } from "../../error-copy";
import {
  type Assistant,
  type Fillsite,
  fleetActions,
  listQueries,
  matches,
  ownedBy,
  PROFILE_STATUS,
  useRefreshFleet,
} from "../../fleet";
import { APPROVAL_STATUS, type ApprovalStatus } from "../../labels";
import { AddressField } from "../shared/address-field";
import { ConfirmDialog } from "../shared/confirm";
import { asText, PointField } from "../shared/point-field";
import { OwnerFilter, SearchBox, useSearchText } from "./filters";
import styles from "./fleet.module.css";
import { type OwnerChoice, OwnerSelect, ownerName } from "./owner-select";
import type { FleetCan, FleetSearch } from "./page";

const noMore = { hasMore: false, loadingMore: false, onLoadMore: () => {} };

interface ViewProps {
  search: FleetSearch;
  set: (patch: Partial<FleetSearch>, replace?: boolean) => void;
  can: FleetCan;
  adding: boolean;
  onAdded: () => void;
}

const STATES = Object.keys(PROFILE_STATUS) as Assistant["status"][];
const APPROVALS = Object.keys(APPROVAL_STATUS) as ApprovalStatus[];

/**
 * Driver assistants: the people who ride with a driver. One list, not paginated, so it's searched
 * and filtered here.
 */
export function AssistantsView({ search, set, can, adding, onAdded }: ViewProps) {
  const assistants = useQuery(listQueries.assistants);
  const [dialog, setDialog] = useState<{ kind: "edit" | "remove"; assistant: Assistant } | null>(null);
  const close = () => setDialog(null);
  const text = useSearchText(search.q, set);
  const rows = (assistants.data ?? []).filter(
    (a) =>
      matches(search.q, [a.name], a.phone) && (!search.state || a.status === search.state) && ownedBy(search.owner)(a),
  );
  return (
    <>
      <ListView
        caption="Assistants"
        itemName={{ one: "assistant", other: "assistants" }}
        rows={rows}
        rowKey={(a) => a.id}
        status={assistants.status}
        errorMessage={assistants.error ? messageFor(errorOf(assistants.error)) : undefined}
        onRetry={() => assistants.refetch()}
        {...noMore}
        filtered={Boolean(search.q || search.state || search.owner)}
        onResetFilters={() => {
          text.clear();
          set({ q: undefined, state: undefined, owner: undefined });
        }}
        toolbar={
          <div className={styles.toolbarEnd}>
            <SearchBox label="Search assistants" placeholder="Name or phone" text={text} />
            <Select
              label="Status"
              value={search.state ?? "any"}
              onValueChange={(v) => set({ state: v === "any" ? undefined : (v as Assistant["status"]) })}
              options={[
                { value: "any", label: "Any status" },
                ...STATES.map((s) => ({ value: s, label: PROFILE_STATUS[s][0] })),
              ]}
            />
            <OwnerFilter value={search.owner} onChange={(owner) => set({ owner })} partners={can.partners} />
          </div>
        }
        empty={{ title: "No assistants yet", description: "Assistants ride with a driver and help carry hoses." }}
        columns={[
          { key: "name", label: "Assistant", render: (a) => <span className={styles.strong}>{a.name}</span> },
          { key: "phone", label: "Phone", render: (a) => formatPhone(a.phone) || "—" },
          { key: "partner_id", label: "Owner", render: (a) => ownerName(a) },
          {
            key: "status",
            label: "Status",
            render: (a) => <Badge tone={PROFILE_STATUS[a.status][1]}>{PROFILE_STATUS[a.status][0]}</Badge>,
          },
          {
            key: "actions",
            label: "",
            render: (a) =>
              can.manage ? (
                <DropdownMenu
                  label="Actions"
                  items={[
                    { label: "Edit", onSelect: () => setDialog({ kind: "edit", assistant: a }) },
                    { label: "Remove", destructive: true, onSelect: () => setDialog({ kind: "remove", assistant: a }) },
                  ]}
                />
              ) : null,
          },
        ]}
      />
      {adding ? <AssistantDialog canChooseOwner={can.partners} onClose={onAdded} /> : null}
      {dialog?.kind === "edit" ? (
        <AssistantDialog assistant={dialog.assistant} canChooseOwner={can.partners} onClose={close} />
      ) : null}
      {dialog?.kind === "remove" ? <RemoveAssistant assistant={dialog.assistant} onClose={close} /> : null}
    </>
  );
}

/** Add an assistant, or edit one; staff who manage partners also choose whose they are (P8-42). */
function AssistantDialog({
  assistant,
  canChooseOwner,
  onClose,
}: {
  assistant?: Assistant;
  canChooseOwner: boolean;
  onClose: () => void;
}) {
  const refresh = useRefreshFleet();
  const [name, setName] = useState(assistant?.name ?? "");
  const [phone, setPhone] = useState(assistant?.phone ?? "");
  const [status, setStatus] = useState(assistant?.status ?? "active");
  const [owner, setOwner] = useState<OwnerChoice>(assistant?.partner_id ?? null);
  const ownerField = canChooseOwner ? { partner_id: owner } : {};
  const mutation = useMutation({
    mutationFn: () =>
      assistant
        ? fleetActions.updateAssistant(assistant.id, {
            name: name.trim(),
            phone: phone.trim() || null,
            status,
            ...ownerField,
          })
        : fleetActions.createAssistant({ name: name.trim(), phone: phone.trim() || undefined, ...ownerField }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={assistant ? `Edit ${assistant.name}` : "Add an assistant"}>
        <div className={styles.form}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Phone (optional)"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+254712345678"
          />
          {assistant ? (
            <Select
              label="Status"
              value={status}
              onValueChange={(v) => setStatus(v as Assistant["status"])}
              options={(Object.keys(PROFILE_STATUS) as Assistant["status"][]).map((s) => ({
                value: s,
                label: PROFILE_STATUS[s][0],
              }))}
            />
          ) : null}
          {canChooseOwner ? <OwnerSelect value={owner} onChange={setOwner} /> : null}
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim()} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {assistant ? "Save" : "Add assistant"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemoveAssistant({ assistant, onClose }: { assistant: Assistant; onClose: () => void }) {
  const refresh = useRefreshFleet();
  return (
    <ConfirmDialog
      title={`Remove ${assistant.name}?`}
      description="They can't be picked for new deliveries. Past deliveries keep them."
      action="Remove"
      keep="Keep them"
      danger
      run={() => fleetActions.removeAssistant(assistant.id)}
      onDone={refresh}
      onClose={onClose}
    />
  );
}

type SiteDialog = { kind: "edit" | "remove" | "approve" | "reject"; site: Fillsite } | null;

/**
 * Fill sites: where trucks load water. Partners' new sites wait for review (`partners.manage`),
 * listed first. Dispatch only offers approved sites in use. One list, not paginated, so it's
 * searched and filtered here.
 */
export function FillsitesView({ search, set, can, adding, onAdded }: ViewProps) {
  const sites = useQuery(listQueries.fillsites);
  const [dialog, setDialog] = useState<SiteDialog>(null);
  const close = () => setDialog(null);
  const text = useSearchText(search.q, set);
  const waiting = (f: Fillsite) => (f.approval_status === "pending" ? 0 : 1);
  const rows = (sites.data ?? [])
    .filter(
      (f) =>
        matches(search.q, [f.name, f.address_line, f.geofence?.name], f.phone) &&
        (!search.approval || f.approval_status === search.approval) &&
        ownedBy(search.owner)(f),
    )
    .sort((a, b) => waiting(a) - waiting(b));
  const menu = (site: Fillsite): DropdownItem[] => {
    const items: DropdownItem[] = [];
    // a decision can be changed later: a rejected site approved, an approved one rejected
    if (can.partners && site.approval_status !== "approved")
      items.push({ label: "Approve", onSelect: () => setDialog({ kind: "approve", site }) });
    if (can.partners && site.approval_status !== "rejected")
      items.push({ label: "Reject", destructive: true, onSelect: () => setDialog({ kind: "reject", site }) });
    if (can.manage) {
      items.push({
        label: "Edit",
        onSelect: () => setDialog({ kind: "edit", site }),
        separatorBefore: items.length > 0,
      });
      items.push({ label: "Remove", destructive: true, onSelect: () => setDialog({ kind: "remove", site }) });
    }
    return items;
  };
  return (
    <>
      <ListView
        caption="Fill sites"
        itemName={{ one: "fill site", other: "fill sites" }}
        rows={rows}
        rowKey={(f) => f.id}
        status={sites.status}
        errorMessage={sites.error ? messageFor(errorOf(sites.error)) : undefined}
        onRetry={() => sites.refetch()}
        {...noMore}
        filtered={Boolean(search.q || search.approval || search.owner)}
        onResetFilters={() => {
          text.clear();
          set({ q: undefined, approval: undefined, owner: undefined });
        }}
        toolbar={
          <div className={styles.toolbarEnd}>
            <SearchBox label="Search fill sites" placeholder="Name, address or zone" text={text} />
            <Select
              label="Approval"
              value={search.approval ?? "any"}
              onValueChange={(v) => set({ approval: v === "any" ? undefined : (v as ApprovalStatus) })}
              options={[
                { value: "any", label: "Any" },
                ...APPROVALS.map((s) => ({ value: s, label: APPROVAL_STATUS[s][0] })),
              ]}
            />
            <OwnerFilter value={search.owner} onChange={(owner) => set({ owner })} partners={can.partners} />
          </div>
        }
        empty={{ title: "No fill sites yet", description: "Add the boreholes and depots your trucks load from." }}
        columns={[
          {
            key: "name",
            label: "Fill site",
            render: (f) => (
              <span className={styles.stack}>
                <span className={styles.strong}>{f.name}</span>
                <span className={styles.muted}>
                  {[f.address_line, formatPhone(f.phone)].filter(Boolean).join(" · ") || "No address"}
                </span>
              </span>
            ),
          },
          {
            key: "geofence",
            label: "Service zone",
            render: (f) =>
              f.geofence ? <Badge tone="info">{f.geofence.name}</Badge> : <Badge tone="warning">Outside zones</Badge>,
          },
          {
            key: "daily_limit_litres",
            label: "Daily limit",
            numeric: true,
            render: (f) => (f.daily_limit_litres === null ? "—" : `${f.daily_limit_litres.toLocaleString("en-KE")} L`),
          },
          { key: "partner_id", label: "Owner", render: (f) => ownerName(f) },
          {
            key: "approval_status",
            label: "Status",
            render: (f) =>
              f.approval_status === "approved" && !f.is_active ? (
                <Badge tone="neutral">Not in use</Badge>
              ) : (
                <Badge tone={APPROVAL_STATUS[f.approval_status][1]}>{APPROVAL_STATUS[f.approval_status][0]}</Badge>
              ),
          },
          {
            key: "actions",
            label: "",
            render: (f) => {
              const items = menu(f);
              return items.length ? <DropdownMenu label="Actions" items={items} /> : null;
            },
          },
        ]}
      />
      {adding ? <FillsiteDialog canChooseOwner={can.partners} onClose={onAdded} /> : null}
      {dialog?.kind === "edit" ? (
        <FillsiteDialog site={dialog.site} canChooseOwner={can.partners} onClose={close} />
      ) : null}
      {dialog?.kind === "remove" ? <RemoveFillsite site={dialog.site} onClose={close} /> : null}
      {dialog?.kind === "approve" || dialog?.kind === "reject" ? (
        <ReviewFillsite site={dialog.site} decision={dialog.kind} onClose={close} />
      ) : null}
    </>
  );
}

/**
 * Add or edit a fill site. Search the address (Google Places, P8-41) to fill it and pin it, or set
 * the point on the map or by typing; the point decides its service zone. Staff who manage partners
 * also choose whose it is (P8-42).
 */
function FillsiteDialog({
  site,
  canChooseOwner,
  onClose,
}: {
  site?: Fillsite;
  canChooseOwner: boolean;
  onClose: () => void;
}) {
  const refresh = useRefreshFleet();
  const [name, setName] = useState(site?.name ?? "");
  const [address, setAddress] = useState(site?.address_line ?? "");
  const [phone, setPhone] = useState(site?.phone ?? "");
  const [lat, setLat] = useState(site?.latitude?.toString() ?? "");
  const [lng, setLng] = useState(site?.longitude?.toString() ?? "");
  const [inUse, setInUse] = useState(site?.is_active ?? true);
  const [limit, setLimit] = useState(site?.daily_limit_litres?.toString() ?? "");
  const limitOk = limit.trim() === "" || /^\d{4,8}$/.test(limit.trim());
  // number-ok: litres, not money
  const dailyLimit = limit.trim() ? Number.parseInt(limit.trim(), 10) : null;
  // staff who manage partners choose whose it is (P8-42)
  const [owner, setOwner] = useState<OwnerChoice>(site?.partner_id ?? null);
  const valid = name.trim() && isCoordinate(lat, 90) && isCoordinate(lng, 180) && limitOk;
  const mutation = useMutation({
    mutationFn: () => {
      const point = { latitude: toCoordinate(lat), longitude: toCoordinate(lng) };
      const ownerField = canChooseOwner ? { partner_id: owner } : {};
      return site
        ? fleetActions.updateFillsite(site.id, {
            name: name.trim(),
            ...point,
            address_line: address.trim() || null,
            phone: phone.trim() || null,
            is_active: inUse,
            daily_limit_litres: dailyLimit,
            ...ownerField,
          })
        : fleetActions.createFillsite({
            name: name.trim(),
            ...point,
            address_line: address.trim() || undefined,
            phone: phone.trim() || undefined,
            daily_limit_litres: dailyLimit ?? undefined,
            ...ownerField,
          });
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={site ? `Edit ${site.name}` : "Add a fill site"}
        description="Search the address to pin it, or click the map. The pin decides which service zone it's in."
      >
        <div className={styles.form}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ruiru borehole" />
          <AddressField
            label="Address (optional)"
            value={address}
            onChange={setAddress}
            onPick={(picked) => {
              setAddress(picked.address);
              setLat(asText(picked.lat));
              setLng(asText(picked.lng));
              // a new fill site is often named after the place itself
              if (!name.trim()) setName(picked.name);
            }}
          />
          <PointField
            lat={lat}
            lng={lng}
            onChange={(la, ln) => {
              setLat(la);
              setLng(ln);
            }}
          />
          <Input
            label="Phone (optional)"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+254712345678"
          />
          <Input
            label="Daily limit, litres (optional)"
            description="What it can supply in a day, 1,000 to 10,000,000 litres. Plan the week shows each day's expected use against it; leave it blank for no limit."
            inputMode="numeric"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="180000"
          />
          {canChooseOwner ? <OwnerSelect value={owner} onChange={setOwner} /> : null}
          {site ? (
            <Switch
              label="In use: Dispatch offers it"
              checked={inUse}
              onCheckedChange={(checked) => setInUse(checked)}
            />
          ) : null}
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!valid} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {site ? "Save" : "Add fill site"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemoveFillsite({ site, onClose }: { site: Fillsite; onClose: () => void }) {
  const refresh = useRefreshFleet();
  return (
    <ConfirmDialog
      title={`Remove ${site.name}?`}
      description="Dispatch stops offering it. Deliveries already loaded there keep it."
      action="Remove"
      keep="Keep it"
      danger
      run={() => fleetActions.removeFillsite(site.id)}
      onDone={refresh}
      onClose={onClose}
    />
  );
}

function ReviewFillsite({
  site,
  decision,
  onClose,
}: {
  site: Fillsite;
  decision: "approve" | "reject";
  onClose: () => void;
}) {
  const refresh = useRefreshFleet();
  return decision === "approve" ? (
    <ConfirmDialog
      title={`Approve ${site.name}?`}
      description="The partner's trucks can load there, and Dispatch offers it."
      action="Approve"
      run={() => fleetActions.reviewFillsite(site.id, "approve")}
      onDone={refresh}
      onClose={onClose}
    />
  ) : (
    <ConfirmDialog
      title={`Reject ${site.name}?`}
      description="Dispatch won't offer it. The partner sees it as rejected."
      action="Reject"
      danger
      run={() => fleetActions.reviewFillsite(site.id, "reject")}
      onDone={refresh}
      onClose={onClose}
    />
  );
}
